import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { AREAS } from "@/data/areas";
import { TOWN_PAGES } from "@/data/townPages";

const SITE = "https://www.vortexvs.com";
const DEFAULT_TITLE = "Vortex Vending Services LLC — Modern Vending for a Modern World";

const MAP = {
  "/": {
    title: "Smart Cooler Vending in West Chester, PA | Vortex Vending Services",
    description: "Smart cooler vending at no cost to your location for offices, apartments, and medical facilities in the western Philadelphia suburbs. Stocked, spotless, fast service.",
  },
  "/about": {
    title: "About Us — Vortex Vending Services LLC",
    description: "We're a West Chester, PA vending company specializing in smart coolers: fast communication, always stocked machines, and spotless amenities.",
  },
  "/contact": {
    title: "Contact Us — Get a Smart Cooler at Your Workplace | Vortex Vending",
    description: "Get a smart cooler at your office, apartment building, or medical facility in the western Philadelphia suburbs. Call (610) 983-8060 or email info@vortexvs.com.",
  },
  "/feedback": {
    title: "Customer Feedback — Vortex Vending Services",
    description: "Current customer? Send product suggestions, restock requests, or report a machine issue — fast communication is our specialty.",
    // Customers reach it from the menu or a machine QR code; in search it only competes with the home page for the brand name.
    noindex: true,
  },
  "/service-area": {
    title: "Service Area: West Chester, PA & Surrounding Towns | Vortex Vending",
    description: "Based in West Chester, PA, Vortex Vending Services serves businesses across 19 surrounding towns. See the full service area and how stocking, cleaning, and maintenance work.",
  },
  "/office-vending": {
    title: "Office Vending Services in West Chester, PA | Vortex Vending",
    description: "Smart cooler vending for offices and workplaces in the West Chester, PA area — break-room convenience, custom product mix, regular stocking and cleaning. Call (610) 983-8060.",
    service: { name: "Office Smart Cooler Vending", description: "Smart cooler vending service for offices and workplaces: placement, regular stocking, cleaning, maintenance, and a custom product mix." },
  },
  "/apartment-building-vending": {
    title: "Apartment Building Vending — Smart Cooler Amenity | Vortex Vending",
    description: "A smart cooler amenity for apartment residents — 24/7 access in lobbies, fitness rooms, and mailrooms with no staffing burden for property managers. West Chester, PA area.",
    service: { name: "Apartment Building Smart Cooler Vending", description: "Smart cooler vending amenity for apartment buildings: resident access in common areas with stocking, cleaning, and maintenance fully handled." },
  },
  "/medical-office-vending": {
    title: "Medical Office Vending — Smart Coolers for Clinics | Vortex Vending",
    description: "Smart cooler vending for medical offices in the West Chester, PA area — drinks and snacks for staff on long shifts and families in waiting rooms, with cleaning and maintenance handled.",
    service: { name: "Medical Office Smart Cooler Vending", description: "Smart cooler vending service for medical offices and clinics: stocked drinks and snacks with full cleaning and maintenance suited to healthcare settings." },
  },
  "/admin": { title: "Vortex Workspace", noindex: true },
};

for (const t of TOWN_PAGES) {
  MAP[`/areas/${t.slug}`] = {
    title: t.title,
    description: t.description,
    service: {
      name: `Smart Cooler Vending in ${t.name}, PA`,
      description: `Smart cooler vending service for offices, apartment buildings, and medical offices in ${t.name}, PA: placement, regular stocking, cleaning, and maintenance.`,
      areaServed: { "@type": "City", name: `${t.name}, PA` },
    },
  };
}

const SeoManager = () => {
  const { pathname } = useLocation();
  let seo = MAP[pathname] || (pathname.startsWith("/admin/") ? MAP["/admin"] : undefined);
  if (!seo) seo = { title: DEFAULT_TITLE, noindex: true };
  const url = `${SITE}${pathname}`;
  const serviceLd = seo.service
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        name: seo.service.name,
        serviceType: "Smart cooler vending service",
        description: seo.service.description,
        url,
        provider: { "@id": `${SITE}/#business` },
        areaServed: seo.service.areaServed || AREAS.map((a) => `${a.name} PA`),
      }
    : null;
  return (
    <Helmet>
      <title>{seo.title}</title>
      {seo.description && <meta name="description" content={seo.description} />}
      <link rel="canonical" href={url} />
      {seo.noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Vortex Vending Services LLC" />
      <meta property="og:title" content={seo.title} />
      {seo.description && <meta property="og:description" content={seo.description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={`${SITE}/images/og-share.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Vortex Vending Services smart cooler" />
      <meta name="twitter:card" content="summary_large_image" />
      {serviceLd && (
        <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
      )}
    </Helmet>
  );
};

export default SeoManager;
